import React, { useEffect, useState } from "react";
import { Tabs, Input, Button, Space, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { SearchOutlined, PlusOutlined } from "@ant-design/icons";
import { format } from "date-fns";

import { fetchSentNotifications, getNotificationType } from "../../../../api/communications/sentNotificationsApi";
import type { SentNotification } from "../../../../types/communications/NotificationTypes";
import {
	NotificationRecipient,
	RELATED_ENTITY_TYPE_PATIENT,
} from "../../../../types/communications/NotificationRecipient";
import NotificationDetailModal from "../../communications/NotificationDetailModal";
import SendManualNotificationDialog from "./submodals/SendManualNotification";
import ScheduledNotificationsTab from "./ScheduledNotificationsTab";
import { useNotifications } from "../../../../context/NotificationContext";
import InlineLoading from "../../../../components/shared/InlineLoading";
import StatusTag from "../../../../components/shared/StatusTag";
import { loadUserPreferences } from "../../../../utils/userPrefs/UserPrefs";
import { DataTable } from "../../../../components/shared";

const { Text } = Typography;

interface SentNotificationsTabProps {
	recipient: NotificationRecipient;
	/**
	 * CU-86d44j2ac: threaded to the Scheduled tab and the send dialog. Both default
	 * to the patient workflow, which is unchanged.
	 */
	relatedEntityType?: string;
	recipientNoun?: string;
}

const SentNotificationsTab: React.FC<SentNotificationsTabProps> = ({
	recipient,
	relatedEntityType = RELATED_ENTITY_TYPE_PATIENT,
	recipientNoun = "patient",
}) => {
	const [notifications, setNotifications] = useState<SentNotification[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [selectedNotification, setSelectedNotification] = useState<SentNotification | null>(null);
	const [detailModalOpen, setDetailModalOpen] = useState<boolean>(false);
	const [manualModalOpen, setManualModalOpen] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const { showError } = useNotifications();
	const PAGE_SIZE = loadUserPreferences().tableRowsPerPage || 10;

	useEffect(() => {
		loadNotifications();
	}, [recipient]);

	const loadNotifications = async () => {
		setIsLoading(true);
		try {
			const filters = recipient.id ? { recipient_user_id: recipient.id } : { recipient_email: recipient.email };
			const raw = await fetchSentNotifications(filters);
			setNotifications(Array.isArray(raw) ? raw : []);
		} catch {
			showError("Failed to load notifications");
		} finally {
			setIsLoading(false);
		}
	};

	const getFiltered = (list: SentNotification[]) => {
		if (!searchTerm) return list;
		const s = searchTerm.toLowerCase();
		// `preview`, not the body: the list API stopped returning `parsed_template` in SEC
		// (86d3uy3fg), so this searches the first 200 characters the server now sends.
		return list.filter((n) => (n.subject || "").toLowerCase().includes(s) || (n.template_name || "").toLowerCase().includes(s) || (n.preview || "").toLowerCase().includes(s));
	};

	const getChannelTag = (record: SentNotification) => {
		const type = getNotificationType(record);
		if (type === "both") {
			return (
				<Space size={4}>
					<StatusTag status="email" />
					<StatusTag status="sms" />
				</Space>
			);
		}
		if (type === "email" || type === "sms" || type === "push") return <StatusTag status={type} />;
		return <StatusTag status="unknown" label="Unknown" />;
	};

	const columns: ColumnsType<SentNotification> = [
		{ title: "Notification Name", dataIndex: "template_name", key: "template_name", sorter: (a, b) => (a.template_name || "").localeCompare(b.template_name || ""), render: (v) => <Text strong>{v}</Text> },
		{ title: "Subject", dataIndex: "subject", key: "subject", render: (v) => <span>{v || "No Subject"}</span> },
		{
			title: "Channel",
			key: "channel",
			width: 140,
			filters: [
				{ text: "Email", value: "email" },
				{ text: "SMS", value: "sms" },
				{ text: "Push", value: "push" },
			],
			onFilter: (value, record) => getNotificationType(record) === value,
			render: (_: any, record) => getChannelTag(record),
		},
		{ title: "Sent At", dataIndex: "created_at", key: "created_at", sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(), render: (v: string) => format(new Date(v), "MMM dd, yyyy hh:mm a") },
	];

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			<div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
				<Button type="primary" icon={<PlusOutlined />} onClick={() => setManualModalOpen(true)}>
					Send Notification
				</Button>
				<Input placeholder="Search notifications" prefix={<SearchOutlined />} allowClear value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ minWidth: 260 }} />
			</div>

			<>
				{isLoading ? (		
					<InlineLoading message="Loading notifications…" minHeight={200} />
				) : (
					<Tabs
						defaultActiveKey="all"
						size="small"
						items={[
							{
								key: "all",
								label: "All Notifications",
								children: (
									<DataTable<SentNotification>
										rowKey={(r) => String(r.id)}
										dataSource={getFiltered(notifications)}
										columns={columns}
										pagination={{ pageSize: PAGE_SIZE }}
										scrollX="max-content"
										onRowClick={(record: SentNotification) => {
											setSelectedNotification(record);
											setDetailModalOpen(true);
										}}
										emptyTitle="No notifications found"
									/>
								),
							},
							{
								key: "scheduled",
								label: "Scheduled",
								children: (
									<ScheduledNotificationsTab
										recipient={recipient}
										relatedEntityType={relatedEntityType}
									/>
								),
							},
						]}
					/>
				)}
			</>

			<SendManualNotificationDialog
				recipient={recipient}
				relatedEntityType={relatedEntityType}
				recipientNoun={recipientNoun}
				isOpen={manualModalOpen}
				onClose={() => {
					setManualModalOpen(false);
					loadNotifications();
				}}
			/>
			<NotificationDetailModal notification={selectedNotification} isOpen={detailModalOpen} onClose={() => setDetailModalOpen(false)} />
		</div>
	);
};

export default SentNotificationsTab;
